import React from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../context/DarkModeContext';

const Page: React.FC = () => {
  const { darkMode } = useDarkMode();

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Link 
              to="/" 
              className={`text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300`}
            >
              ← Back to Home
            </Link>
          </div>
          
          <h1 className="text-4xl font-bold mb-8">Terms and Conditions</h1>
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Agreement to Terms</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                By accessing and using CareerCard, you agree to be bound by these Terms and Conditions and our Privacy Policy. 
                If you disagree with any part of these terms, you may not access the service. These Terms of Service constitute 
                a legally binding agreement between you and CareerCard regarding your use of the service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                CareerCard is a career management platform that provides tools for tracking professional achievements, 
                managing resumes, and facilitating job searches. The service includes AI-powered features for career development 
                and professional growth tracking. We reserve the right to modify, suspend, or discontinue any aspect of the service 
                at any time without notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Eligibility</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                You must be at least 18 years old to use CareerCard. By using the service, you represent and warrant that you have 
                the right, authority, and capacity to enter into these Terms and to abide by all of the terms and conditions set forth herein.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. User Accounts</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                You must create an account to use CareerCard. You are responsible for:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Ensuring your account information is accurate and up-to-date</li>
                <li>Immediately notifying us of any unauthorized use of your account</li>
              </ul>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We reserve the right to disable your account if we determine that you have violated these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. User Content</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                You retain all rights to any content you submit to CareerCard. By submitting content, you grant us a worldwide, 
                non-exclusive, royalty-free license to use, store, and process this content for the purpose of providing our services.
                You represent and warrant that:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>You own or have the necessary rights to your content</li>
                <li>Your content does not violate any third party's intellectual property rights</li>
                <li>Your content does not violate any applicable laws or regulations</li>
                <li>Your content is accurate and truthful</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. AI Features and Data Processing</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Our AI-powered features process your career data to provide personalized insights and recommendations. 
                This processing is conducted in accordance with our Privacy Policy and applicable data protection laws.
                You acknowledge that:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>AI recommendations are suggestions and not guaranteed outcomes</li>
                <li>The accuracy of AI insights depends on the accuracy of provided data</li>
                <li>AI features may be updated or modified over time</li>
                <li>We may use anonymized data to improve our AI systems</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Intellectual Property Rights</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                The service and its original content (excluding user-submitted content), features, and functionality are owned 
                by CareerCard and are protected by international copyright, trademark, patent, trade secret, and other 
                intellectual property laws. You agree not to reproduce, distribute, modify, create derivative works of, publicly 
                display, publicly perform, republish, download, store, or transmit any material from our service without our consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Acceptable Use</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                You agree not to use CareerCard to:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Violate any applicable laws or regulations</li>
                <li>Impersonate any person or entity</li>
                <li>Harass, abuse, or harm another person</li>
                <li>Upload malicious code or interfere with the service's security</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Engage in data mining or scraping without our consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Privacy and Data Protection</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We take your privacy seriously. Our collection and use of personal information is governed by our 
                Privacy Policy. By using CareerCard, you consent to our data practices as described in the Privacy Policy.
                We implement appropriate technical and organizational measures to protect your personal data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Third-Party Services</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                CareerCard may integrate with or contain links to third-party services. We are not responsible for any 
                third-party content, services, or practices. Your use of third-party services is subject to their respective 
                terms and policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Limitation of Liability</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                To the maximum extent permitted by law, CareerCard shall not be liable for any indirect, incidental, special, 
                consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, 
                or any loss of data, use, goodwill, or other intangible losses resulting from your use of our service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Termination</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We may terminate or suspend your account and access to the service immediately, without prior notice, for:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Violations of these Terms</li>
                <li>Conduct harmful to other users, us, or third parties</li>
                <li>Fraudulent or illegal activities</li>
                <li>Non-payment of any fees owed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">13. Changes to Terms</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We reserve the right to modify these terms at any time. We will notify users of any material changes 
                via email or through the service. Your continued use of CareerCard after such modifications constitutes 
                your acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">14. Governing Law</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                These Terms shall be governed by and construed in accordance with the laws of the United States, 
                without regard to its conflict of law provisions. Any disputes arising under or in connection with 
                these Terms shall be subject to the exclusive jurisdiction of the courts located within the United States.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">15. Contact Information</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                If you have any questions about these Terms, please contact us at:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Email: support@careercard.com</li>
              </ul>
            </section>

            <div className={`mt-8 pt-8 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Last updated: March 2025
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Page; 